/**
 * SOLID Review
 * S — Acceptable. Everything gameplay-generic the 3D scene and the 2D cup share: the cup's two moves, the rattles, shake to roll (the
 *     sensor's probe, iOS's permission, the threshold and cooldown) and its debug overlay, the WebGL debug overlay's refresh, the rules
 *     modal, and the chrome's texts (the must-beat line, the table code, the hints). The shake detection is the largest part.
 * O — Good. A third rendering technology needs only another cup view over the same props; this does not change.
 * L — Strong. A view model (`IServiceViewModel`).
 * I — Good. The scene's snapshot, the cup's tap, the permission ask, the rules modal's two actions.
 * D — Good. The Application's ports, the request contexts, the dice sound, the platform's timers, clock, address and motion sensor,
 *     and the WebGL probe's answers.
 */
import type { IServiceViewModel } from './IServiceViewModel';
import { ObservableState } from './ObservableState';
import { Reactions, type IConfigReaction } from './Reactions';
import type { IConfigDepViewPlatform, TViewTimer } from './platform/IConfigDepViewPlatform';
import type { DiceAudio } from './DiceAudio';
import type { ClientRequests } from '../client/ClientRequests';
import { fmtScore } from '../utils/fmtScore';
import { GamePhase } from '../../application/ports/ClientVocabulary';
import type { IPortClientStateView, TClientViewState } from '../../application/ports/ClientStateView';
import type { IPortClientUseCases, TClientUseCaseInput } from '../../application/ports/ClientUseCases.contract';

/** The cup's two moves carry no command: the table names the round and the seat. */
const PEEK: TClientUseCaseInput<'peekDice'> = {};
const ROLL: TClientUseCaseInput<'rollDice'> = {};
/** A shake: the acceleration's magnitude above this, once per gesture (one gesture oscillates across it for 1–2 s). */
const SHAKE_THRESHOLD = 16;
const SHAKE_COOLDOWN_MS = 1500;
/** A blind peek's cosmetic shake lasts this long. */
const PEEK_SHAKE_MS = 500;
/** `?shakeDebug`: no motion event within this long reads as "not supported". */
const SHAKE_DEBUG_SILENCE_MS = 3000;
/** `?gldebug`: the overlay is redrawn this often, so the canvas's live counters show. */
const GL_DEBUG_TICK_MS = 500;

type TView = TClientViewState;

/** The cup view's props — the 3D canvas's cup or the 2D one — as the scene hands them (`FallbackCupScene.tsx`'s until P4). */
export interface IPropsCupScene {
  phase: GamePhase;
  isActiveRoller: boolean;
  isShaking: boolean;
  diceValues: { die1: number; die2: number } | null;
  diceRevealed: boolean;
  onCupTap: () => void;
}

/** What the scene reads of the client view — the selectors it held, one by one. */
interface IStateGameSceneReads {
  readonly phase: TView['phase'];
  readonly room: TView['room'];
  readonly activeRollerId: TView['activeRollerId'];
  readonly minimumClaimScore: TView['minimumClaimScore'];
  readonly lastDiceRoll: TView['lastDiceRoll'];
  readonly diceRevealed: TView['diceRevealed'];
  readonly isActiveRoller: TView['derived']['isActiveRoller'];
  readonly cupAction: TView['derived']['cupAction'];
}

interface IStateMotionDebug {
  readonly supported: boolean | null;
  readonly count: number;
  readonly lastMag: number;
}

interface IStateGameSceneOwn {
  readonly rolling: boolean;
  readonly isShaking: boolean;
  readonly shakeEnabled: boolean;
  readonly needsShakePermission: boolean;
  readonly glTick: number;
  readonly motionDebug: IStateMotionDebug;
  readonly rulesOpen: boolean;
}

/** The scene as its view renders it. */
export interface IStateGameScene {
  readonly phase: TView['phase'];
  readonly isActiveRoller: boolean;
  readonly diceRevealed: boolean;
  readonly cupAction: TView['derived']['cupAction'];
  readonly rolling: boolean;
  readonly isShaking: boolean;
  readonly shakeEnabled: boolean;
  readonly needsShakePermission: boolean;
  readonly rulesOpen: boolean;
  /** The cup view's props — the 3D canvas's or the 2D cup's. */
  readonly cupScene: IPropsCupScene;
  /** "<who> must beat: <score>", while a floor stands in ROLLING or PEEKING. */
  readonly mustBeat: string | null;
  /** The table's code once the game has started. */
  readonly tableCode: string | null;
  readonly showOpponentRollHint: boolean;
  readonly showPeekHint: boolean;
  readonly showRollHint: boolean;
  readonly rollHint: string;
  /** `?gldebug`: the WebGL probe's lines; `?shakeDebug`: the sensor's. */
  readonly glDebugText: string | null;
  readonly shakeDebugText: string | null;
  /** Bumped every half second under `?gldebug`, so the canvas's live counters are drawn again. */
  readonly glTick: number;
}

/** The answers of the WebGL probe `?gldebug` shows (`webglSupport.probeWebGL`). */
export interface IConfigGlDebug {
  readonly webglAvailable: boolean;
  readonly probe: () => Record<string, string>;
}

export interface IConfigDepGameScene {
  readonly useCases: Pick<IPortClientUseCases, 'rollDice' | 'peekDice'>;
  readonly view: IPortClientStateView;
  readonly requests: ClientRequests;
  readonly diceAudio: DiceAudio;
  readonly platform: Pick<IConfigDepViewPlatform, 'timers' | 'clock' | 'address' | 'motion'>;
  readonly glDebug: IConfigGlDebug;
}

const SHAKING_ROLLING: Partial<IStateGameSceneOwn> = { isShaking: true, rolling: true };
const STILL_IDLE: Partial<IStateGameSceneOwn> = { isShaking: false, rolling: false };
const SHAKING: Partial<IStateGameSceneOwn> = { isShaking: true };
const STILL: Partial<IStateGameSceneOwn> = { isShaking: false };
const SENSOR_CONFIRMED: Partial<IStateGameSceneOwn> = { shakeEnabled: true };
const PERMITTED: Partial<IStateGameSceneOwn> = { shakeEnabled: true, needsShakePermission: false };
const RULES_OPEN: Partial<IStateGameSceneOwn> = { rulesOpen: true };
const RULES_CLOSED: Partial<IStateGameSceneOwn> = { rulesOpen: false };

/**
 * THE GAME SCENE (frontend-framework-oo P4, 2026-10-29, §28.296) — `components/game/GameSceneCore.tsx`'s state, effects and handlers;
 * the view keeps the chrome's markup and hands the cup view its props. Who may tap and what a tap does is the Domain's
 * (`derived.cupAction`, strict-ports FE1e). The six effects are reactions in the component's order: the `?gldebug` refresh, the
 * rattle every other seat hears when the table moves to PEEKING on someone else's roll (a table that MOUNTS in PEEKING heard nothing),
 * the sensor's probe (its first event confirms shake-to-roll, however late it comes — some tablets take seconds), `?shakeDebug`'s own
 * listener, the shake listener (a roll in ROLLING; in a blind PEEKING a shake only rattles — peeking needs a tap), and the shake's end
 * when the phase leaves ROLLING. The shake listener is re-armed whenever what a tap would do changes, as it was.
 */
export class GameSceneViewModel implements IServiceViewModel<IStateGameScene> {
  private readonly state: ObservableState<IStateGameSceneOwn>;
  private readonly reactions: Reactions<IStateGameScene>;
  private readonly shakeDebug: boolean;
  private readonly glDebugText: string | null;
  /** The phase the rattle's reaction last saw — the table's at mount. */
  private lastPhase: TView['phase'];
  /** The last shake that counted — kept across the listener's re-arming, so one gesture is one shake. */
  private lastShakeTrigger = 0;
  private reads: IStateGameSceneReads | null = null;
  private shown: { readonly reads: IStateGameSceneReads; readonly own: IStateGameSceneOwn; readonly scene: IStateGameScene } | null = null;

  constructor(private readonly deps: IConfigDepGameScene) {
    const { motion, address } = deps.platform;
    const flags = new URLSearchParams(address.search());
    this.shakeDebug = flags.has('shakeDebug');
    this.glDebugText = flags.has('gldebug') ? GameSceneViewModel.glDebugLines(deps.glDebug) : null;
    this.lastPhase = deps.view.getSnapshot().phase;
    const initial: IStateGameSceneOwn = {
      rolling: false, isShaking: false, shakeEnabled: false, needsShakePermission: motion.permissionRequired(), glTick: 0,
      motionDebug: { supported: null, count: 0, lastMag: 0 }, rulesOpen: false,
    };
    this.state = new ObservableState<IStateGameSceneOwn>(initial);
    const list: Array<IConfigReaction<IStateGameScene>> = [
      { deps: () => [], run: () => this.glDebugTick() },
      { deps: (s) => [s.phase, s.isActiveRoller], run: (s) => this.hearTheRoll(s) },
      { deps: (s) => [s.needsShakePermission], run: (s) => this.probeSensor(s) },
      { deps: () => [], run: () => this.shakeDebugListener() },
      { deps: (s) => [s.shakeEnabled, s.isActiveRoller, s.phase, s.diceRevealed, s.cupAction, s.rolling], run: (s) => this.shakeListener(s) },
      { deps: (s) => [s.phase], run: (s) => { if (s.phase !== GamePhase.ROLLING) this.state.update(STILL); } },
    ];
    this.reactions = new Reactions<IStateGameScene>(list);
  }

  /** `?gldebug`'s overlay: what the browser's WebGL answered when the page loaded, and when the probe asked. */
  private static glDebugLines(glDebug: IConfigGlDebug): string {
    const glProbe = glDebug.probe();
    return [
      `module.WEBGL_AVAILABLE=${glDebug.webglAvailable}`,
      `probe webgl2=${glProbe.webgl2} webgl1=${glProbe.webgl1}`,
      `renderer=${glProbe.renderer ?? '-'}`,
      `glVersion=${glProbe.glVersion ?? '-'}`,
      `glsl=${glProbe.glsl ?? '-'} maxTex=${glProbe.maxTex ?? '-'}`,
      glProbe.probeError ? `probeError=${glProbe.probeError}` : '',
    ].filter(Boolean).join('\n');
  }

  private update(change: Partial<IStateGameSceneOwn>): void {
    this.state.update(change);
  }

  // ── the reactions ───────────────────────────────────────────────────────────────────────────────────────────────────

  private glDebugTick(): (() => void) | undefined {
    if (this.glDebugText === null) return undefined;
    const { timers } = this.deps.platform;
    const id = timers.setInterval(() => {
      const tick: Partial<IStateGameSceneOwn> = { glTick: this.state.get().glTick + 1 };
      this.update(tick);
    }, GL_DEBUG_TICK_MS);
    return () => timers.clearInterval(id);
  }

  /** Every other seat HEARS the roll: the table moved to PEEKING on someone else's roll. */
  private hearTheRoll(s: IStateGameScene): void {
    if (s.phase === GamePhase.PEEKING && this.lastPhase !== GamePhase.PEEKING && !s.isActiveRoller) this.deps.diceAudio.rattle(0.9);
    this.lastPhase = s.phase;
  }

  /** Whether motion events actually fire here — `typeof` is not enough (Chrome on http blocks them on real devices). */
  private probeSensor(s: IStateGameScene): (() => void) | undefined {
    const { motion } = this.deps.platform;
    if (s.needsShakePermission) return undefined; // iOS handles via explicit permission button
    if (!motion.available()) return undefined;
    let confirmed = false;
    const leave = motion.on(() => {
      if (!confirmed) {
        confirmed = true;
        this.update(SENSOR_CONFIRMED);
        leave();
      }
    });
    return leave;
  }

  /** `?shakeDebug`: whether motion events arrive at all, and how hard — independent of the gating above. */
  private shakeDebugListener(): (() => void) | undefined {
    if (!this.shakeDebug) return undefined;
    const { motion, timers } = this.deps.platform;
    if (!motion.available()) {
      const unsupported: Partial<IStateGameSceneOwn> = { motionDebug: { ...this.state.get().motionDebug, supported: false } };
      this.update(unsupported);
      return undefined;
    }
    const leave = motion.on((e) => {
      const a = e.accelerationIncludingGravity;
      const mag = a ? Math.sqrt((a.x ?? 0) ** 2 + (a.y ?? 0) ** 2 + (a.z ?? 0) ** 2) : 0;
      const arrived: Partial<IStateGameSceneOwn> = { motionDebug: { supported: true, count: this.state.get().motionDebug.count + 1, lastMag: mag } };
      this.update(arrived);
    });
    const timeout = timers.setTimeout(() => {
      const d = this.state.get().motionDebug;
      if (d.count !== 0) return;
      const silent: Partial<IStateGameSceneOwn> = { motionDebug: { ...d, supported: false } };
      this.update(silent);
    }, SHAKE_DEBUG_SILENCE_MS);
    return () => {
      leave();
      timers.clearTimeout(timeout);
    };
  }

  /** The roller's shakes: a roll in ROLLING; in a blind PEEKING only the rattle — peeking needs a tap. */
  private shakeListener(s: IStateGameScene): (() => void) | undefined {
    if (!s.shakeEnabled || !s.isActiveRoller) return undefined;
    const { motion, timers, clock } = this.deps.platform;
    const timeouts: TViewTimer[] = [];
    const leave = motion.on((e) => {
      const a = e.accelerationIncludingGravity;
      if (!a) return;
      const mag = Math.sqrt((a.x ?? 0) ** 2 + (a.y ?? 0) ** 2 + (a.z ?? 0) ** 2);
      const now = clock.now();
      if (mag <= SHAKE_THRESHOLD || now - this.lastShakeTrigger <= SHAKE_COOLDOWN_MS) return;
      this.lastShakeTrigger = now;
      if (s.phase === GamePhase.ROLLING) {
        void this.cupTap();
      } else if (s.phase === GamePhase.PEEKING && !s.diceRevealed) {
        this.update(SHAKING);
        this.deps.diceAudio.rattle(0.7);
        timeouts.push(timers.setTimeout(() => this.update(STILL), PEEK_SHAKE_MS));
      }
    });
    return () => {
      leave();
      timeouts.forEach((timer) => timers.clearTimeout(timer));
    };
  }

  // ── the actions ─────────────────────────────────────────────────────────────────────────────────────────────────────

  /**
   * A tap on the cup — or a shake in ROLLING. Second tap in a blind PEEKING: ask the server for the dice (the roller only; a refusal
   * changes nothing). First tap in ROLLING: roll — with the in-flight guard, since the phase advances only when the server answers and
   * under load a second tap would send a duplicate (idempotent at the server, but extra load when it is already struggling).
   */
  readonly cupTap = async (): Promise<void> => {
    const { cupAction } = this.deps.view.getSnapshot().derived;
    if (cupAction === 'peek') {
      await this.deps.useCases.peekDice(this.deps.requests.context(), PEEK);
      return;
    }
    if (cupAction !== 'roll') return;
    if (this.state.get().rolling) return;
    this.update(SHAKING_ROLLING);
    this.deps.diceAudio.rattle(1.1);
    try {
      // Dice NOT stored locally — the server keeps them secret until the peek.
      await this.deps.useCases.rollDice(this.deps.requests.context(), ROLL);
    } finally {
      this.update(STILL_IDLE);
    }
  };

  /** iOS 13+: shake-to-roll asked for from a tap. */
  readonly requestShakePermission = async (): Promise<void> => {
    try {
      const result = await this.deps.platform.motion.requestPermission();
      if (result === 'granted') this.update(PERMITTED);
    } catch { /* permission denied */ }
  };

  readonly openRules = (): void => this.update(RULES_OPEN);
  readonly closeRules = (): void => this.update(RULES_CLOSED);

  // ── the snapshot ────────────────────────────────────────────────────────────────────────────────────────────────────

  private readsOf(view: TView): IStateGameSceneReads {
    const next: IStateGameSceneReads = {
      phase: view.phase,
      room: view.room,
      activeRollerId: view.activeRollerId,
      minimumClaimScore: view.minimumClaimScore,
      lastDiceRoll: view.lastDiceRoll,
      diceRevealed: view.diceRevealed,
      isActiveRoller: view.derived.isActiveRoller,
      cupAction: view.derived.cupAction,
    };
    const before = this.reads;
    const keys = Object.keys(next) as Array<keyof IStateGameSceneReads>;
    if (before !== null && keys.every((key) => Object.is(before[key], next[key]))) return before;
    this.reads = next;
    return next;
  }

  getSnapshot(): IStateGameScene {
    const reads = this.readsOf(this.deps.view.getSnapshot());
    const own = this.state.get();
    if (this.shown !== null && this.shown.reads === reads && this.shown.own === own) return this.shown.scene;
    const scene = this.sceneOf(reads, own);
    this.shown = { reads, own, scene };
    return scene;
  }

  private sceneOf(reads: IStateGameSceneReads, own: IStateGameSceneOwn): IStateGameScene {
    const { phase, room, isActiveRoller, diceRevealed, minimumClaimScore, lastDiceRoll } = reads;
    const { needsShakePermission, shakeEnabled, motionDebug } = own;
    const roller = isActiveRoller ? 'You' : room?.players.find((p) => p.id === reads.activeRollerId)?.displayName ?? 'Roller';
    const cupScene: IPropsCupScene = {
      phase, isActiveRoller, isShaking: own.isShaking, diceValues: lastDiceRoll ? { die1: lastDiceRoll.die1, die2: lastDiceRoll.die2 } : null,
      diceRevealed, onCupTap: this.cupTap,
    };
    return {
      phase, isActiveRoller, diceRevealed, cupAction: reads.cupAction, rolling: own.rolling, isShaking: own.isShaking, shakeEnabled,
      needsShakePermission, rulesOpen: own.rulesOpen, cupScene,
      mustBeat: (phase === GamePhase.ROLLING || phase === GamePhase.PEEKING) && minimumClaimScore > 0
        ? `${roller} must beat: ${fmtScore(minimumClaimScore)}` : null,
      tableCode: room?.roomCode && phase !== GamePhase.LOBBY && phase !== GamePhase.IDLE ? room.roomCode : null,
      showOpponentRollHint: phase === GamePhase.ROLLING && !isActiveRoller,
      showPeekHint: phase === GamePhase.PEEKING && isActiveRoller && !diceRevealed,
      showRollHint: phase === GamePhase.ROLLING && isActiveRoller,
      rollHint: needsShakePermission
        ? 'Tap here to enable shake-to-roll'
        : shakeEnabled ? 'Tap or shake the cup to roll' : 'Tap the cup to roll',
      glDebugText: this.glDebugText,
      shakeDebugText: this.shakeDebug
        ? `needsShakePermission: ${needsShakePermission}
shakeEnabled: ${shakeEnabled}
motion supported: ${motionDebug.supported === null ? '…probing' : motionDebug.supported}
events received: ${motionDebug.count}
last magnitude: ${motionDebug.lastMag.toFixed(2)} (threshold 16)`
        : null,
      glTick: own.glTick,
    };
  }

  subscribe(listener: () => void): () => void {
    const leaveOwn = this.state.subscribe(listener);
    const leaveView = this.deps.view.subscribe(listener);
    return () => {
      leaveOwn();
      leaveView();
    };
  }

  attach(): void {}

  detach(): void {
    this.reactions.stop();
  }

  release(_inputs: readonly unknown[], rendered: IStateGameScene): void {
    this.reactions.stopChanged(rendered);
  }

  commit(_inputs: readonly unknown[], rendered: IStateGameScene): void {
    this.reactions.startChanged(rendered);
  }
}
